<script>
  import { tick } from 'svelte';
  import { fade } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import Desktop from './lib/shell/Desktop.svelte';
  import MobileShell from './lib/shell/mobile/MobileShell.svelte';
  import LoginScreen from './lib/shell/LoginScreen.svelte';
  import BootSplash from './lib/shell/BootSplash.svelte';
  import { playClick } from './lib/os/sound.svelte.js';
  import Siri from './lib/shell/Siri.svelte';
  import SystemDialogs from './lib/shell/SystemDialogs.svelte';
  import NotificationCenter from './lib/shell/NotificationCenter.svelte';
  import {
    activeId,
    closeWindow,
    injectSpacesRef,
    launchApp,
    minimizeWindow,
    restoreSession,
  } from './lib/os/windows.svelte.js';
  import { loadSession } from './lib/os/persist.svelte.js';
  import { spacesState, restoreSpaces } from './lib/os/spaces.svelte.js';
  import { loginGuest } from './lib/os/session.svelte.js';
  import { toggleSiri } from './lib/os/commands.svelte.js';
  import { APPS } from './lib/os/apps.js';
  import { initializeFilesystem } from './lib/os/filesystem.svelte.js';
  import { initializeCalendarReminders } from './lib/os/reminders.js';
  import { verifySession } from './lib/os/session.svelte.js';
  import { portfolio, syncFromAPI } from './lib/os/portfolio.svelte.js';
  import { startVisitorTracking } from './lib/os/visitor.svelte.js';

  let step = $state('boot'); // 'boot' | 'login' | 'desktop' | 'shutdown' | 'restart' | 'sleep'
  let powerMessage = $state('');
  let isMobile = $state(globalThis.innerWidth < 600);

  // animate-ui-style page reveal: opacity + blur + a whisper of scale.
  // The in/out layers are stacked (position:fixed), so screens cross-dissolve
  // in place instead of shoving each other around.
  function pageBlur(node, { duration = 620, delay = 0, from = 1.06, blur = 14 } = {}) {
    if (portfolio.settings.reduceMotion) return { duration: 0, css: (t) => `opacity:${t}` };
    return {
      duration,
      delay,
      easing: cubicOut,
      css: (t, u) => `
        opacity: ${t};
        filter: blur(${u * blur}px);
        transform: scale(${from + (1 - from) * t});
      `,
    };
  }

  $effect(() => {
    const handler = () => {
      isMobile = globalThis.innerWidth < 600;
    };
    globalThis.addEventListener('resize', handler);
    return () => globalThis.removeEventListener('resize', handler);
  });

  // Give windows.svelte.js a reference to spacesState so it can include
  // spaces in the session snapshot without a circular import.
  injectSpacesRef(spacesState);

  // Boot lingers so the splash animation + startup chime can breathe.
  const BOOT_MS = portfolio.settings.reduceMotion ? 1200 : 5400;
  $effect(() => {
    const id = setTimeout(async () => {
      await verifySession();
      await initializeFilesystem();
      await initializeCalendarReminders();
      syncFromAPI();
      step = 'login';
    }, BOOT_MS);
    return () => clearTimeout(id);
  });

  // Retro click sound on every pressable thing in the OS.
  $effect(() => {
    const clickables = 'button, [role="button"], a, select, input[type="checkbox"], input[type="radio"]';
    const handler = (e) => {
      if (e.target?.closest?.(clickables)) playClick();
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  });

  async function handleLogin(role) {
    if (role === 'guest') loginGuest();
    const session = loadSession();
    // Guest Radar: begin invisible, privacy-lite visit tracking once the
    // desktop initialises. Fails silently and respects the opt-out flag.
    startVisitorTracking();
    step = 'desktop';
    // tick(), not requestAnimationFrame: rAF never fires in a hidden tab, which
    // silently suspended the startup launches forever (blank desktop).
    await tick();
    if (session?.windows?.length) {
      if (session.spaces) restoreSpaces(session.spaces, session.activeSpaceId);
      await restoreSession(session);
    } else {
      // Each launch is isolated so a failed component load (e.g. a dev-server
      // reload mid-login) can never abort the other startup window.
      const welcomeApp = APPS.find((a) => a.id === 'welcome');
      const aboutMeApp = APPS.find((a) => a.id === 'about-me');
      try {
        if (welcomeApp) await launchApp(welcomeApp, {}, { title: 'Welcome', x: 90, y: 70 });
      } catch (e) { console.error('[startup] welcome launch failed', e); }
      try {
        if (aboutMeApp) await launchApp(aboutMeApp, {}, { title: 'About Me', x: 580, y: 60 });
      } catch (e) { console.error('[startup] about-me launch failed', e); }
    }
  }

  function shortcut(action) {
    const user = portfolio.settings.shortcuts?.[action];
    const defaults = {
      finder: { key: 'n', meta: true },
      'close-window': { key: 'w', meta: true },
      minimize: { key: 'm', meta: true },
      siri: { key: 'k', meta: true },
    };
    return user ?? defaults[action] ?? null;
  }

  function keyMatch(e, action) {
    const s = shortcut(action);
    if (!s) return false;
    if (e.key.toLowerCase() !== s.key.toLowerCase() && e.key !== s.key) return false;
    if (s.meta && !e.metaKey && !e.ctrlKey) return false;
    if (s.ctrl && !e.ctrlKey) return false;
    if (s.alt && !e.altKey) return false;
    if (s.shift && !e.shiftKey) return false;
    if (!s.meta && !s.ctrl && !s.alt && !s.shift) {
      return !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey;
    }
    return true;
  }

  function handleGlobalKeydown(e) {
    if (keyMatch(e, 'siri')) {
      e.preventDefault();
      toggleSiri();
    } else if (keyMatch(e, 'minimize')) {
      e.preventDefault();
      const id = activeId();
      if (id) minimizeWindow(id);
    } else if (keyMatch(e, 'close-window')) {
      e.preventDefault();
      const id = activeId();
      if (id) closeWindow(id);
    } else if (keyMatch(e, 'finder')) {
      e.preventDefault();
      launchApp(APPS.find((app) => app.id === 'projects'));
    }
  }
</script>

<svelte:window
  onkeydown={handleGlobalKeydown}
  on:os-power={(e) => {
    const action = e.detail?.action;
    if (action === 'shutdown') {
      powerMessage = 'Shutting down...';
      step = 'shutdown';
      setTimeout(() => location.reload(), 3000);
    } else if (action === 'restart') {
      powerMessage = 'Restarting...';
      step = 'restart';
      setTimeout(() => location.reload(), 2500);
    } else if (action === 'sleep') {
      powerMessage = 'Sleeping...';
      step = 'sleep';
      setTimeout(() => {
        step = 'login';
        powerMessage = '';
      }, 4000);
    }
  }}
/>

{#key step}
  {#if step === 'desktop'}
    {#if isMobile}
      <MobileShell />
    {:else}
      <div class="page" in:pageBlur={{ from: 0.94, blur: 10, duration: 680, delay: 120 }}>
        <Desktop />
      </div>
    {/if}
    <Siri />
    <SystemDialogs />
    <NotificationCenter />
  {:else if step === 'login'}
    <div class="page" in:pageBlur={{ from: 1.05, delay: 120 }} out:pageBlur={{ from: 0.97, duration: 340 }}>
      <LoginScreen onlogin={handleLogin} />
    </div>
  {:else if step === 'shutdown' || step === 'restart' || step === 'sleep'}
    <div class="page power-screen" in:pageBlur={{ from: 1.04 }} out:fade={{ duration: 300 }}>
      <svg class="boot-apple" viewBox="0 0 384 512" aria-hidden="true">
        <path
          fill="currentColor"
          d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C72.7 141.9 32 175.6 32 244.1c0 20.2 3.7 41.1 11.1 62.6 9.9 28.3 45.6 97.7 82.8 96.6 19.4-.5 33.1-13.8 58.4-13.8 24.5 0 37.2 13.8 58.5 13.8 37.6-.5 69.9-63.7 79.4-92.1-50.4-23.8-47.9-69.7-47.9-71.5zM262.7 113.3c20.4-24.7 18.5-47.2 17.9-55.3-17.9 1-38.6 12.2-50.4 26-13 14.9-20.6 33.3-19 52.9 19.4 1.5 37.2-8.5 51.5-23.6z"
        />
      </svg>
      <p class="power-message">{powerMessage}</p>
      {#if step === 'shutdown'}
        <div class="power-bar"><div class="power-bar-fill shutdown"></div></div>
      {:else if step === 'restart'}
        <div class="power-bar"><div class="power-bar-fill restart"></div></div>
      {:else}
        <div class="power-bar pulse"><div class="power-bar-fill sleep"></div></div>
      {/if}
    </div>
  {:else}
    <div class="page" in:fade={{ duration: 300 }} out:pageBlur={{ from: 1.04, blur: 18, duration: 560 }}>
      <BootSplash />
    </div>
  {/if}
{/key}

<style>
  .boot-apple {
    width: 64px;
    height: 78px;
    color: #3a3a3a;
  }

  /* Stacked transition layers so screens cross-dissolve in place
     instead of pushing each other around during the {#key} swap. */
  .page {
    position: fixed;
    inset: 0;
    transform-origin: center;
    will-change: opacity, filter, transform;
  }

  .power-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    background: #0a0a0a;
  }
  .power-message {
    font: 16px var(--font);
    color: #8a8a8a;
    margin: 0;
    letter-spacing: 0.5px;
  }
  .power-bar {
    width: 200px;
    height: 4px;
    border-radius: 2px;
    overflow: hidden;
    background: #1a1a1a;
  }
  .power-bar.pulse {
    animation: power-pulse 2s ease-in-out infinite;
  }
  @keyframes power-pulse {
    0%,
    100% {
      opacity: 0.3;
    }
    50% {
      opacity: 1;
    }
  }
  .power-bar-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 2s ease;
  }
  .power-bar-fill.shutdown {
    width: 100%;
    background: #c44;
    animation: power-drain 3s ease forwards;
  }
  .power-bar-fill.restart {
    width: 100%;
    background: #48a;
    animation: power-drain 2.5s ease forwards;
  }
  .power-bar-fill.sleep {
    width: 30%;
    background: #686;
    animation: power-breathe 2s ease-in-out infinite;
  }
  @keyframes power-drain {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
  @keyframes power-breathe {
    0%,
    100% {
      width: 30%;
    }
    50% {
      width: 60%;
    }
  }
</style>
